import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DOCUMENT_KIND_CHOICES, DOCUMENT_KIND_LABELS, INCOME_PAYER_TYPES, findDuplicateIncome, type DocumentKindChoice, type Income, type IncomeInput, type IncomePreview } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, extractIncomeFile, saveIncome, type IncomeExtractionResponse } from "../services/expensesService";
import { Chip, Dialog, ErrorBox, Field, FileDrop, Notice, Spinner, Tabs } from "./ui";
import { currentDay, money } from "../lib/format";
import { IMPORT_PATH } from "../lib/incomeRoutes";
import { ui } from "../theme";

// Registrar un ingreso (#1274): a mano o adjuntando el comprobante para que lo
// lea la IA. El documento es un estado de cuenta (varios depósitos, se eligen
// cuáles registrar) o una sola operación (llena el formulario). El tipo se elige
// en el dropdown y además lo identifica la IA. Nada se guarda sin revisar.

const NS = "expensesStatements";

export type IncomeDialogMode = "manual" | "ia";

type Form = { date: string; amount: string; payer: string; client: string; payerType: string; concept: string };
// La fecha por defecto es la de Ciudad de México (como el backend), no la UTC.
const emptyForm = (): Form => ({ date: currentDay(), amount: "", payer: "", client: "", payerType: "cliente", concept: "" });

/** Lo que la IA leyó y el formulario no muestra: viaja tal cual al guardar. */
type Extras = Pick<IncomeInput, "currency" | "exchangeRate" | "bank" | "account">;

type Row = IncomePreview & { selected: boolean };

const kindLabel = (kind: DocumentKindChoice, language: string) => DOCUMENT_KIND_LABELS[kind][language.startsWith("en") ? "en" : "es"];

export default function IncomeDialog({ open, initialMode = "manual", onClose, onSaved }: { open: boolean; initialMode?: IncomeDialogMode; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const { t, i18n } = useTranslation(["common", NS]);
  const tt = (k: string, o?: Record<string, unknown>) => t(`${NS}:incomeAi.${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const lang = i18n.language;

  const [mode, setMode] = useState<IncomeDialogMode>(initialMode);
  const [form, setForm] = useState<Form>(emptyForm);
  const [extras, setExtras] = useState<Extras>({});
  const [kind, setKind] = useState<DocumentKindChoice>("auto");
  const [busy, setBusy] = useState<"" | "reading" | "saving">("");
  const [error, setError] = useState<string | null>(null);
  const [read, setRead] = useState<IncomeExtractionResponse | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  /** El formulario se llenó con lo que leyó la IA (una sola operación). */
  const [prefilled, setPrefilled] = useState<IncomePreview | null>(null);

  // Cada lectura lleva su número: una que termina tarde (el modal se cerró, se
  // reabrió o se pidió otra) no pisa lo que hay en pantalla.
  const readId = useRef(0);

  useEffect(() => {
    readId.current += 1;
    if (!open) return;
    setMode(initialMode);
    setForm(emptyForm());
    setExtras({});
    setKind("auto");
    setBusy("");
    setError(null);
    setRead(null);
    setRows([]);
    setPrefilled(null);
  }, [open, initialMode]);

  // Lo que la IA puso en el formulario se va con su lectura: si se quedara, se
  // guardaría sin la moneda ni la cuenta del documento del que salió.
  const resetAi = () => {
    setRead(null);
    setRows([]);
    if (prefilled) setForm(emptyForm());
    setPrefilled(null);
    setExtras({});
    setError(null);
  };

  // Mientras se guarda no se cierra: un lote a medias seguiría guardando a ciegas.
  const close = () => {
    if (busy !== "saving") onClose();
  };

  const readFile = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const id = ++readId.current;
    setBusy("reading");
    setError(null);
    try {
      const res = await extractIncomeFile(file, kind);
      if (id !== readId.current) return;
      setRead(res);
      const only = res.incomes.length === 1 ? res.incomes[0] : null;
      if (res.documentType === "operacion" && only) {
        // Una sola operación: se revisa en el formulario de siempre.
        setForm({ date: only.date || currentDay(), amount: String(only.amount), payer: only.payer, client: "", payerType: only.payerType, concept: only.concept || "" });
        setExtras({ currency: only.currency, exchangeRate: only.exchangeRate, bank: only.bank, account: res.account });
        setPrefilled(only);
        setRows([]);
        setMode("manual");
      } else {
        if (prefilled) setForm(emptyForm());
        setPrefilled(null);
        setExtras({});
        setRows(res.incomes.map((i) => ({ ...i, selected: !i.duplicateOf && Boolean(i.date) })));
      }
    } catch (err) {
      if (id === readId.current) setError(apiErrorMessage(err) || t("expenses.errors.extract"));
    } finally {
      if (id === readId.current) setBusy("");
    }
  };

  const saveOne = async () => {
    setBusy("saving");
    setError(null);
    try {
      await saveIncome({
        date: form.date,
        amount: Number(form.amount),
        payer: form.payer,
        client: form.client.trim() || null,
        payerType: form.payerType as Income["payerType"],
        concept: form.concept || null,
        ...(prefilled ? extras : {}),
      });
      onClose();
      await onSaved();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setBusy("");
    }
  };

  const patch = (index: number, p: Partial<Row>) => setRows((rs) => rs.map((r) => (r.index === index ? { ...r, ...p } : r)));
  // Al corregir la fecha se vuelve a buscar el duplicado; si aparece, la fila se desmarca.
  const setDate = (index: number, date: string | null) =>
    setRows((rs) =>
      rs.map((r) => {
        if (r.index !== index) return r;
        const duplicateOf = findDuplicateIncome({ date, amount: r.amount, currency: r.currency }, read?.matches || []);
        return { ...r, date, duplicateOf, selected: duplicateOf && !r.duplicateOf ? false : r.selected && Boolean(date) };
      }),
    );
  const selected = rows.filter((r) => r.selected);
  const canSaveMany = selected.length > 0 && selected.every((r) => r.date && r.payer.trim());

  const saveMany = async () => {
    setBusy("saving");
    setError(null);
    const done: number[] = [];
    try {
      // De uno en uno: si alguno falla, los ya guardados salen de la lista y no se repiten.
      for (const r of selected) {
        await saveIncome({
          date: r.date as string,
          amount: r.amount,
          currency: r.currency,
          exchangeRate: r.exchangeRate,
          payer: r.payer.trim(),
          payerType: r.payerType,
          concept: r.concept,
          bank: r.bank,
          account: read?.account || null,
          source: read?.documentType === "estado_de_cuenta" ? "estado_cuenta" : "manual",
        });
        done.push(r.index);
      }
      onClose();
      await onSaved();
    } catch (err) {
      setRows((rs) => rs.filter((r) => !done.includes(r.index)));
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
      if (done.length) void onSaved();
    } finally {
      setBusy("");
    }
  };

  const listing = mode === "ia" && read != null && !prefilled;
  const manualValid = Boolean(form.payer) && Number(form.amount) > 0;

  return (
    <Dialog
      open={open}
      wide={listing && rows.length > 0}
      title={t("expenses.cashflow.addIncome")}
      onClose={close}
      footer={
        <>
          <button type="button" className={c.secondary} disabled={busy === "saving"} onClick={close}>
            {t("expenses.cancel")}
          </button>
          {mode === "manual" && (
            <button type="button" className={c.primary} disabled={Boolean(busy) || !manualValid} onClick={saveOne}>
              {busy === "saving" ? t("expenses.saving") : t("expenses.save")}
            </button>
          )}
          {listing && rows.length > 0 && (
            <button type="button" className={c.primary} disabled={Boolean(busy) || !canSaveMany} onClick={saveMany}>
              {busy === "saving" ? t("expenses.saving") : tt("saveMany", { count: selected.length })}
            </button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        <Tabs<IncomeDialogMode>
          label={tt("mode")}
          value={mode}
          onChange={(m) => !busy && setMode(m)}
          tabs={[
            { id: "manual", label: tt("manual"), icon: "ri-edit-line" },
            { id: "ia", label: tt("ai"), icon: "ri-magic-line" },
          ]}
        />
        <ErrorBox message={error} />

        {mode === "manual" && (
          <>
            {prefilled && read && (
              <Notice tone={prefilled.duplicateOf || prefilled.confidence === "baja" ? "warn" : "info"}>
                <p>
                  <i className="ri-magic-line mr-1" />
                  {tt("prefilled", { file: read.fileName })}
                </p>
                {extras.currency && extras.currency !== "MXN" && (
                  <p className="mt-1">{extras.exchangeRate ? tt("currencyRate", { currency: extras.currency, rate: extras.exchangeRate }) : tt("currencyNoRate", { currency: extras.currency })}</p>
                )}
                {prefilled.duplicateOf && <p className="mt-1 font-medium">{tt("duplicateOne")}</p>}
                {prefilled.confidence === "baja" && <p className="mt-1">{tt("lowConfidence")}</p>}
                {read.mismatch && read.detectedType && <p className="mt-1">{tt("mismatch", { detected: kindLabel(read.detectedType, lang) })}</p>}
              </Notice>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t("expenses.fields.date")}>
                <input id="income-date" type="date" className={c.input} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </Field>
              <Field label={t("expenses.fields.amount")}>
                <input id="income-amount" type="number" min="0" step="0.01" inputMode="decimal" className={c.input} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </Field>
              <Field label={t("expenses.cashflow.payer")} className="sm:col-span-2">
                <input id="income-payer" className={c.input} value={form.payer} onChange={(e) => setForm({ ...form, payer: e.target.value })} />
              </Field>
              <Field label={t("expenses.cashflow.client")}>
                <input id="income-client" className={c.input} placeholder={t("expenses.cashflow.clientHint")} value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} />
              </Field>
              <Field label={t("expenses.cashflow.payerTypeLabel")}>
                <select id="income-payer-type" className={c.input} value={form.payerType} onChange={(e) => setForm({ ...form, payerType: e.target.value })}>
                  {INCOME_PAYER_TYPES.map((p) => (
                    <option key={p} value={p}>
                      {t(`expenses.cashflow.payerType.${p}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("expenses.cashflow.concept")}>
                <input id="income-concept" className={c.input} value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} />
              </Field>
            </div>
          </>
        )}

        {mode === "ia" && !listing && (
          <div className="space-y-3" data-testid="income-ai-upload">
            <p className={`text-sm ${c.muted}`}>{tt("intro")}</p>
            <Field label={tt("kind")}>
              <select id="income-doc-kind" className={c.input} value={kind} disabled={Boolean(busy)} onChange={(e) => setKind(e.target.value as DocumentKindChoice)}>
                {DOCUMENT_KIND_CHOICES.map((k) => (
                  <option key={k} value={k}>
                    {kindLabel(k, lang)}
                  </option>
                ))}
              </select>
            </Field>
            <p className={`text-xs ${c.muted}`}>{tt(`kindHint.${kind}`)}</p>
            <FileDrop accept="image/png,image/jpeg,image/webp,application/pdf" disabled={Boolean(busy)} label={tt("drop")} hint={tt("dropHint")} onFiles={readFile} />
            {busy === "reading" && <Spinner label={t("expenses.import.reading")} />}
            {prefilled && (
              <button type="button" className={`text-sm text-teal-600 hover:underline`} onClick={() => setMode("manual")}>
                {tt("backToForm")}
              </button>
            )}
          </div>
        )}

        {listing && read && (
          <div className="space-y-3" data-testid="income-ai-list">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Chip className="bg-teal-500/15 text-teal-600">{kindLabel(read.documentType, lang)}</Chip>
              <span className={c.muted}>{[read.issuer, read.account, read.fileName].filter(Boolean).join(" · ")}</span>
              <button type="button" className="ml-auto text-sm text-teal-600 hover:underline" disabled={Boolean(busy)} onClick={resetAi}>
                {tt("another")}
              </button>
            </div>
            {read.mismatch && read.detectedType && <Notice tone="warn">{tt("mismatch", { detected: kindLabel(read.detectedType, lang) })}</Notice>}
            {read.notes && <Notice>{read.notes}</Notice>}
            {rows.length === 0 ? (
              <Notice tone="warn">{tt("none")}</Notice>
            ) : (
              <>
                {rows.some((r) => r.duplicateOf) && <Notice tone="warn">{tt("duplicates", { count: rows.filter((r) => r.duplicateOf).length })}</Notice>}
                {selected.some((r) => r.currency !== "MXN" && !r.exchangeRate) && <Notice tone="warn">{tt("noRateNotice")}</Notice>}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className={`text-left text-xs ${c.muted}`}>
                        <th className="py-1 pr-2" />
                        <th className="py-1 pr-2">{t("expenses.fields.date")}</th>
                        <th className="py-1 pr-2">{t("expenses.cashflow.payer")}</th>
                        <th className="py-1 pr-2">{t("expenses.cashflow.payerTypeLabel")}</th>
                        <th className="py-1 text-right">{t("expenses.fields.amount")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.index} className={`border-t ${c.border} ${c.text} ${r.selected ? "" : "opacity-60"}`} data-testid={`income-row-${r.index}`}>
                          <td className="py-1.5 pr-2">
                            <input type="checkbox" aria-label={`select-income-${r.index}`} className="h-4 w-4 accent-teal-600" checked={r.selected} onChange={(e) => patch(r.index, { selected: e.target.checked })} />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input type="date" aria-label={`date-income-${r.index}`} className={`${c.inputBase} w-36 ${r.date ? "" : "border-amber-500"}`} value={r.date || ""} onChange={(e) => setDate(r.index, e.target.value || null)} />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input aria-label={`payer-income-${r.index}`} className={c.input} value={r.payer} onChange={(e) => patch(r.index, { payer: e.target.value })} />
                            {r.concept && <p className={`mt-0.5 text-xs ${c.muted}`}>{r.concept}</p>}
                          </td>
                          <td className="py-1.5 pr-2">
                            <select aria-label={`type-income-${r.index}`} className={c.input} value={r.payerType} onChange={(e) => patch(r.index, { payerType: e.target.value as Income["payerType"] })}>
                              {INCOME_PAYER_TYPES.map((p) => (
                                <option key={p} value={p}>
                                  {t(`expenses.cashflow.payerType.${p}`)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="whitespace-nowrap py-1.5 text-right font-semibold tabular-nums">{money(r.amount, r.currency, lang)}</td>
                          <td className="space-x-1 whitespace-nowrap py-1.5 pl-2">
                            {r.duplicateOf && <Chip className="bg-amber-500/15 text-amber-600">{t("expenses.import.duplicate")}</Chip>}
                            {r.confidence === "baja" && <Chip className="bg-red-500/15 text-red-600">{t("expenses.import.lowConfidence")}</Chip>}
                            {r.currency !== "MXN" && !r.exchangeRate && <Chip className="bg-amber-500/15 text-amber-600">{tt("noRate")}</Chip>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {read.documentType === "estado_de_cuenta" && (
                  <p className={`text-xs ${c.muted}`}>
                    {tt("statementExpenses")}{" "}
                    {/* En otra pestaña: aquí hay una propuesta sin guardar. */}
                    <a href={IMPORT_PATH} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline">
                      {t("expenses.menu.import")}
                    </a>
                  </p>
                )}
              </>
            )}
            {busy === "saving" && <Spinner label={t("expenses.saving")} />}
          </div>
        )}
      </div>
    </Dialog>
  );
}
